import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, CircularProgress, Box, Typography } from '@mui/material';
import { Placar } from '../../types/placar';
import { atualizarConfiguracoesPartida } from '../../services/partidaService';
import { limparPlacar } from '../../services/placarService';
import { getTiebreakOptions, parseTiebreakValue, formatTiebreakValue } from '../../utils/tiebreakOptions';

interface EditMatchFormatDialogProps {
  open: boolean;
  partidaId: string;
  placarAtual?: Placar & { id?: string } | null;
  numeroSets?: number | null;
  gamesPorSets?: number | null;
  tiebreaks?: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditMatchFormatDialog: React.FC<EditMatchFormatDialogProps> = ({
  open,
  partidaId,
  placarAtual,
  numeroSets: initialNumeroSets,
  gamesPorSets: initialGamesPorSets,
  tiebreaks: initialTiebreaks,
  onClose,
  onSuccess,
}) => {
  const [numeroSets, setNumeroSets] = useState<string>(initialNumeroSets?.toString() || '');
  const [gamesPorSets, setGamesPorSets] = useState<string>(initialGamesPorSets?.toString() || '');
  const [tiebreaks, setTiebreaks] = useState<string>(
    initialGamesPorSets ? formatTiebreakValue(initialTiebreaks) : ''
  );
  const [clearingPlacar, setClearingPlacar] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Verificar se precisa limpar placar antes de mostrar o form
  useEffect(() => {
    if (open) {
      const hasResultadoFinal = placarAtual?.resultadoFinalA != null || placarAtual?.resultadoFinalB != null;
      setShowForm(!hasResultadoFinal);
      setNumeroSets(initialNumeroSets?.toString() || '');
      setGamesPorSets(initialGamesPorSets?.toString() || '');
      setTiebreaks(initialGamesPorSets ? formatTiebreakValue(initialTiebreaks) : '');
      setError(null);
    }
  }, [open, placarAtual, initialNumeroSets, initialGamesPorSets, initialTiebreaks]);

  const handleLimparPlacar = async () => {
    if (!placarAtual?.id) return;
    
    setClearingPlacar(true);
    setError(null);
    try {
      await limparPlacar(placarAtual.id);
      setShowForm(true);
      // Recarregar dados após limpar placar
      onSuccess();
    } catch (err: any) {
      console.error('Erro ao limpar placar:', err);
      setError(err.message || 'Erro ao limpar placar');
    } finally {
      setClearingPlacar(false);
    }
  };

  const handleSave = async () => {
    // Validar se houve mudanças
    const newNumeroSets = numeroSets ? parseInt(numeroSets, 10) : null;
    const newGamesPorSets = gamesPorSets ? parseInt(gamesPorSets, 10) : null;
    const newTiebreaks = parseTiebreakValue(tiebreaks);
    
    const hasChanges = 
      newNumeroSets !== (initialNumeroSets ?? null) ||
      newGamesPorSets !== (initialGamesPorSets ?? null) ||
      newTiebreaks !== (initialTiebreaks ?? null);
    
    if (!hasChanges) {
      setError('Nenhuma alteração foi feita');
      return;
    }
    
    setSaving(true);
    setError(null);
    try {
      const config = {
        numeroSets: newNumeroSets,
        gamesPorSets: newGamesPorSets,
        tiebreaks: newTiebreaks,
      };
      await atualizarConfiguracoesPartida(partidaId, config);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Erro ao salvar configurações:', err);
      setError(err.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  // Usar helper compartilhado para opções de tiebreak
  const tiebreakOptions = getTiebreakOptions(gamesPorSets);

  const hasResultadoFinal = placarAtual?.resultadoFinalA != null || placarAtual?.resultadoFinalB != null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Alterar Formato da Partida</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!showForm && hasResultadoFinal && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Esta partida possui resultado final. Para alterar o formato, é necessário apagar o resultado primeiro.
            </Alert>
            {clearingPlacar ? (
              <Box display="flex" alignItems="center" gap={2}>
                <CircularProgress size={20} />
                <Typography>Limpando placar...</Typography>
              </Box>
            ) : (
              <Button
                variant="outlined"
                color="warning"
                onClick={handleLimparPlacar}
                fullWidth
              >
                Apagar Resultado
              </Button>
            )}
          </Box>
        )}

        {showForm && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Select de Número de Sets */}
            <Box>
              <label htmlFor="numeroSets" className="form-label">
                Sets
              </label>
              <select
                id="numeroSets"
                className="form-select"
                value={numeroSets}
                onChange={(e) => setNumeroSets(e.target.value)}
              >
                {[1, 2, 3, 4, 5].map((num) => (
                  <option key={num} value={num.toString()}>
                    {(() => {
                      switch (num) {
                        case 1:
                          return '1 Set';
                        case 2:
                          return '2 Sets + Super Tiebreak';
                        case 4:
                          return '4 Sets + Super Tiebreak';
                        default:
                          return `${num} Sets`;
                      }
                    })()}
                  </option>
                ))}
              </select>
            </Box>

            {/* Select de Games por Set */}
            <Box>
              <label htmlFor="gamesPorSets" className="form-label">
                Games
              </label>
              <select
                id="gamesPorSets"
                className="form-select"
                value={gamesPorSets}
                onChange={(e) => {
                  setGamesPorSets(e.target.value);
                  // Reset tiebreak quando gamesPorSets mudar, pois as opções mudam
                  if (!e.target.value || e.target.value === '') {
                    setTiebreaks('');
                  } else {
                    setTiebreaks('null'); // Resetar para "Sem tiebreak"
                  }
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((num) => (
                  <option key={num} value={num.toString()}>
                    {num} {num === 1 ? 'Game' : 'Games'}
                  </option>
                ))}
              </select>
            </Box>

            {/* Select de Tiebreak */}
            <Box>
              <label htmlFor="tiebreaks" className="form-label">
                Tiebreak
              </label>
              <select
                id="tiebreaks"
                className="form-select"
                value={tiebreaks}
                onChange={(e) => setTiebreaks(e.target.value)}
                disabled={!gamesPorSets || gamesPorSets === ''}
              >
                {tiebreakOptions.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || clearingPlacar}>
          Cancelar
        </Button>
        {showForm && (
          <Button onClick={handleSave} variant="contained" disabled={saving || clearingPlacar}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EditMatchFormatDialog;

